import { faBed, faLocationDot, faTimes } from '@fortawesome/free-solid-svg-icons';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCities, getTourDates, getTypes } from 'services/package';
import { SelectGrouped } from 'ts/package/interfaces/Package';
import FormGroupDatepicker from 'components/ui/FormGroupDatepicker/FormGroupDatepicker';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { Select } from 'ts/interfaces/Generic';
import Button from 'components/ui/Button/Button';
import { SearchDate } from 'ts/package/interfaces/Package';
import { getDisabledDates } from 'utils/package';
import { addDays, formatPriceCurrency } from 'utils/utils';
import { onClickClose, onClickOverlay } from 'utils/responsive';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { SearchTourModel } from 'ts/package/classes/SearchTourModel';
import { dateFormat } from 'utils/shared/utils';
import * as idb from 'utils/shared/idbUrl';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import useRecaptcha from 'hooks/useRecaptcha';
import { MainContext } from 'contexts/MainContext';
import { b2b } from 'constants/generic';

const SearchTours: FC = () => {
  const type = useRef('eTOURS');
  const [t, i18n] = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [roomTypes, setRoomTypes] = useState<Select[]>([]);
  const [cities, setCities] = useState<SelectGrouped[]>([]);
  const [searchModel, setSearchModel] = useState(new SearchTourModel());
  const [departureDates, setDepartureDates] = useState<SearchDate[]>();
  const [urlParams] = useSearchParams();
  const { userSiteConfig } = useContext(MainContext);
  const { isRecaptchaReady, handleRecapcha } = useRecaptcha();
  const isResultPage = location.pathname.includes('results');
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  useEffect(() => {
    if (!isRecaptchaReady) return;
    init();
  }, [isRecaptchaReady]); // eslint-disable-line

  useEffect(() => {
    if (i18n.language && currentLanguage !== i18n.language) {
      setCurrentLanguage(i18n.language);
      init();
    }
  }, [i18n.language]); // eslint-disable-line

  const init = async () => {
    const searchTypes = await getTypes();

    const newCities = await getCities(type.current);
    if (newCities) {
      setCities(newCities);

      setSearchModel((prevSearchModel) => ({
        ...prevSearchModel,
        cityCode: newCities[0].options.length ? (newCities[0].options[0].value as string) : '',
      }));
    }

    if (searchTypes?.roomTypes) {
      setRoomTypes(searchTypes.roomTypes);

      setSearchModel((prevSearchModel) => ({
        ...prevSearchModel,
        roomType: searchTypes.roomTypes[1].value as string,
      }));
    }
    if (urlParams.get('checkIn')) {
      const objParams = Object.fromEntries(urlParams.entries());
      const cityCode = objParams.cityCode.split('-')[0];
      setSearchModel(new SearchTourModel(urlParams));

      const token = await handleRecapcha();
      if (!token) return;
      const newDepartureDates = await getTourDates(
        cityCode,
        searchModel.flightSourcesDefault,
        token
      );
      setDepartureDates(newDepartureDates || []);
    } else if (newCities) {
      const token = await handleRecapcha();
      if (!token) return;
      const newDepartureDates = await getTourDates(
        (newCities[0].options.length ? (newCities[0].options[0].value as string) : '').split(
          '-'
        )[0],
        searchModel.flightSourcesDefault,
        token
      );
      setDepartureDates(newDepartureDates || []);
      if (newDepartureDates?.length) {
        setSearchModel((prev) => ({ ...prev, checkIn: newDepartureDates[0].date }));
      }
    }
  };

  const onChangeCity = async (value: string, newCities: SelectGrouped[]) => {
    setSearchModel((prevSearchModel) => {
      const group = newCities.find((item) => item.options.some((opt) => opt.value === value));
      const city = group?.options.find((opt) => opt.value === value)?.label;

      return {
        ...prevSearchModel,
        city: city || '',
        type: type.current,
        cityCode: value,
        checkIn: undefined,
      };
    });
    setDepartureDates(undefined);

    const recaptchaToken = await handleRecapcha();
    if (!recaptchaToken) return;
    const newDepartureDates = await getTourDates(
      value.split('-')[0],
      searchModel.flightSourcesDefault,
      recaptchaToken
    );
    setDepartureDates(newDepartureDates || []);
    if (newDepartureDates?.length) {
      onChangeDepartureDate(newDepartureDates[0].date);
    }
  };

  const onChangeDepartureDate = async (value: Date) => {
    setSearchModel((prevSearchModel) => {
      return { ...prevSearchModel, checkIn: value };
    });
  };

  const onClickSearch = () => {
    idb.deletePageStateByPartialKey('tours');
    if (searchModel.checkIn) {
      const searchParams = new URLSearchParams({
        cityCode: searchModel.cityCode,
        checkIn: dateFormat(searchModel.checkIn, 'yyyy-MM-dd'),
        roomType: searchModel.roomType,
        city: searchModel.city,
        flightSourcesDefault: searchModel.flightSourcesDefault,
      }).toString();

      const path = `/tours/results`;

      if (location.search.slice(1) !== searchParams) {
        navigate({ pathname: path, search: searchParams });
      } else {
        window.location.reload();
      }
    }
  };

  const onClickNewSearch = () => {
    navigate('/tours');
    idb.deletePageStateByPartialKey('tours');
    setTimeout(() => {
      const latestPageUrl = localStorage.getItem('latestPageUrl');
      if (latestPageUrl) {
        const latestPageUrlObj = JSON.parse(latestPageUrl);
        localStorage.setItem('latestPageUrl', JSON.stringify({ ...latestPageUrlObj, tour: [] }));
      }
    }, 300);
  };

  return (
    <div className="search-wrapper" onClick={onClickOverlay}>
      <div className="search-inner">
        <div className="results-responsive-header">
          <span className="title text-md">{t('search.search')}</span>
          <FontAwesomeIcon icon={faTimes} onClick={onClickClose} />
        </div>
        {isResultPage && (b2b || userSiteConfig.base.rememberModeEnabled) && (
          <div className="search-element radius">
            <Button
              title={t('home.new_search')}
              onClick={onClickNewSearch}
              className="btn btn-default"
            />
          </div>
        )}
        <div className="search-element tour">
          <SelectCustom
            options={cities}
            value={searchModel.cityCode}
            onChange={(value) => onChangeCity(value, cities)}
            movablePlaceholder={t('search.destination')}
            movablePlaceholderIcon={faLocationDot}
            isLoading={!cities.length}
          />
        </div>
        <div className="search-element tour">
          <FormGroupDatepicker
            onChange={onChangeDepartureDate}
            value={searchModel.checkIn}
            label={t('flight.departing')}
            minDate={departureDates?.length ? departureDates[0]?.date : addDays(1)}
            maxDate={
              departureDates?.length ? departureDates[departureDates?.length - 1]?.date : new Date()
            }
            disabledDates={getDisabledDates(departureDates)}
            longFormat={true}
            dayContentRenderer={(date) => {
              if (!departureDates?.length) return null;

              const depDate = departureDates.find((item) => item.date.getTime() === date.getTime());

              if (!depDate) return null;

              return (
                <span className="rdrDayCustom">
                  <span className="date">{depDate.date.getDate()}</span>
                  <span className="price">{formatPriceCurrency(depDate.minPrice, '$')}</span>
                </span>
              );
            }}
            disabled={!departureDates}
            movablePlaceholder={t('flight.departing')}
            movablePlaceholderIcon={faCalendar}
            isLoading={!departureDates?.length}
            onClickReset={() =>
              setSearchModel({ ...searchModel, checkIn: departureDates?.[0]?.date })
            }
          />
        </div>
        <div className="search-element">
          <SelectCustom
            options={roomTypes}
            value={searchModel.roomType}
            onChange={(value) => setSearchModel({ ...searchModel, roomType: value })}
            movablePlaceholder={t('package.room_type')}
            movablePlaceholderIcon={faBed}
            isLoading={!roomTypes.length}
          />
        </div>

        <div className="search-button">
          <button className="btn btn-primary btn-go" onClick={onClickSearch}>
            GO
          </button>
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(SearchTours, 'SearchTours');
